/**
 * admin接口配置
 */
import type { ApiEndpoint } from '../types';
import { API_CATEGORIES } from '../constants';

export const adminEndpoints: ApiEndpoint[] = [
  {
    id: "admin-list",
    method: "GET",
    path: "/api/admin/list",
    name: "获取管理员列表",
    description: "分页查询管理员列表，支持多条件搜索",
    category: API_CATEGORIES.ADMIN,
    queryParams: [
      { name: "current", type: "number", required: false, description: "当前页码", example: "1" },
      { name: "pageSize", type: "number", required: false, description: "每页条数", example: "10" },
      { name: "admin_account", type: "string", required: false, description: "管理员账号（模糊搜索）" },
      { name: "account_status", type: "string", required: false, description: "账号状态（0-禁用，1-启用）" },
      { name: "is_super_admin", type: "string", required: false, description: "是否超级管理员（true/false）" },
      { name: "role_name", type: "string", required: false, description: "角色名称（模糊搜索）" },
      { name: "created_date_range", type: "string", required: false, description: "创建时间范围（格式：startDate,endDate）" },
      { name: "last_login_date_range", type: "string", required: false, description: "最后登录时间范围（格式：startDate,endDate）" },
    ],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data.items", type: "array", description: "管理员列表" },
      { name: "data.total", type: "number", description: "总记录数" },
      { name: "data.pagination", type: "object", description: "分页信息" },
      { name: "data.items[].id", type: "string", description: "管理员ID" },
      { name: "data.items[].account", type: "string", description: "管理员账号" },
      { name: "data.items[].username", type: "string", description: "管理员用户名" },
      { name: "data.items[].status", type: "number", description: "账号状态（0-禁用，1-启用）" },
      { name: "data.items[].is_super_admin", type: "boolean", description: "是否超级管理员" },
      { name: "data.items[].created_at", type: "string", description: "创建时间" },
      { name: "data.items[].last_login_at", type: "string", description: "最后登录时间" },
    ],
    requiredPermission: "msadmin:admins:view",
    responseExample: JSON.stringify({
      success: true,
      message: "查询成功",
      data: {
        items: [
          {
            id: "1",
            account: "admin",
            username: "管理员",
            status: 1,
            is_super_admin: true,
            created_at: "2025-01-01T00:00:00.000Z",
            last_login_at: "2025-01-20T10:30:00.000Z",
          },
        ],
        total: 10,
        pagination: {
          current: 1,
          pageSize: 10,
          total: 10,
        },
      },
    }, null, 2),
  },
  {
    id: "admin-stats",
    method: "GET",
    path: "/api/admin/stats",
    name: "获取管理员统计信息",
    description: "获取管理员总数、在线数等统计信息",
    category: API_CATEGORIES.ADMIN,
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data.total", type: "number", description: "管理员总数" },
      { name: "data.active", type: "number", description: "启用的管理员数" },
      { name: "data.disabled", type: "number", description: "禁用的管理员数" },
      { name: "data.super_admin", type: "number", description: "超级管理员数" },
    ],
    requiredPermission: "msadmin:admins:view",
    responseExample: JSON.stringify({
      success: true,
      message: "查询成功",
      data: {
        total: 10,
        active: 8,
        disabled: 2,
        super_admin: 1,
      },
    }, null, 2),
  },
  {
    id: "admin-create",
    method: "POST",
    path: "/api/admin/add",
    name: "创建管理员",
    description: "创建新的管理员账号",
    category: API_CATEGORIES.ADMIN,
    bodyParams: [
      { name: "username", type: "string", required: true, description: "用户名", example: "张三" },
      { name: "account", type: "string", required: true, description: "账号", example: "zhangsan" },
      { name: "password", type: "string", required: true, description: "密码" },
      { name: "status", type: "number", required: false, description: "状态（0-禁用，1-启用）", example: "1" },
      { name: "is_super_admin", type: "boolean", required: false, description: "是否超级管理员", example: "false" },
      { name: "role_ids", type: "array", required: false, description: "角色ID数组", example: "[1, 2]" },
    ],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data.id", type: "string", description: "新创建的管理员ID" },
      { name: "data.account", type: "string", description: "管理员账号" },
      { name: "data.username", type: "string", description: "管理员用户名" },
    ],
    requiredPermission: "msadmin:admins:create",
    responseExample: JSON.stringify({
      success: true,
      message: "管理员创建成功",
      data: {
        id: "1",
        account: "zhangsan",
        username: "张三",
      },
    }, null, 2),
  },
  {
    id: "admin-detail",
    method: "GET",
    path: "/api/admin/:id",
    name: "获取管理员详情",
    description: "根据ID获取管理员的详细信息",
    category: API_CATEGORIES.ADMIN,
    pathParams: [{ name: "id", type: "string", required: true, description: "管理员ID" }],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data.id", type: "string", description: "管理员ID" },
      { name: "data.username", type: "string", description: "用户名" },
      { name: "data.account", type: "string", description: "账号" },
      { name: "data.status", type: "number", description: "状态（0-禁用，1-启用）" },
      { name: "data.is_super_admin", type: "boolean", description: "是否超级管理员" },
      { name: "data.created_at", type: "string", description: "创建时间" },
      { name: "data.last_login_at", type: "string", description: "最后登录时间" },
    ],
    requiredPermission: "msadmin:admins:view",
  },
  {
    id: "admin-update",
    method: "PUT",
    path: "/api/admin/:id",
    name: "编辑管理员",
    description: "更新管理员信息",
    category: API_CATEGORIES.ADMIN,
    pathParams: [{ name: "id", type: "string", required: true, description: "管理员ID" }],
    bodyParams: [
      { name: "username", type: "string", required: false, description: "用户名" },
      { name: "status", type: "number", required: false, description: "状态（0-禁用，1-启用）" },
      { name: "is_super_admin", type: "boolean", required: false, description: "是否超级管理员" },
    ],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data.id", type: "string", description: "管理员ID" },
      { name: "data.username", type: "string", description: "用户名" },
      { name: "data.account", type: "string", description: "账号" },
    ],
    requiredPermission: "msadmin:admins:update",
  },
  {
    id: "admin-delete",
    method: "DELETE",
    path: "/api/admin/:id",
    name: "删除管理员",
    description: "根据ID删除管理员",
    category: API_CATEGORIES.ADMIN,
    pathParams: [{ name: "id", type: "string", required: true, description: "管理员ID" }],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
    ],
    requiredPermission: "msadmin:admins:delete",
  },
  {
    id: "admin-batch-delete",
    method: "POST",
    path: "/api/admin/batch-delete",
    name: "批量删除管理员",
    description: "根据ID列表批量删除管理员",
    category: API_CATEGORIES.ADMIN,
    bodyParams: [{ name: "ids", type: "array", required: true, description: "管理员ID数组", example: "[\"1\", \"2\", \"3\"]" }],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data.deletedCount", type: "number", description: "删除的管理员数量" },
    ],
    requiredPermission: "msadmin:admins:delete",
  },
  {
    id: "admin-reset-password",
    method: "POST",
    path: "/api/admin/:id/reset-pwd",
    name: "重置管理员密码",
    description: "重置指定管理员的密码",
    category: API_CATEGORIES.ADMIN,
    pathParams: [{ name: "id", type: "string", required: true, description: "管理员ID" }],
    bodyParams: [{ name: "password", type: "string", required: true, description: "新密码" }],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
    ],
    requiredPermission: "msadmin:admins:update",
  },
  {
    id: "admin-update-status",
    method: "PUT",
    path: "/api/admin/:id/status",
    name: "更新管理员状态",
    description: "启用或禁用管理员账号",
    category: API_CATEGORIES.ADMIN,
    pathParams: [{ name: "id", type: "string", required: true, description: "管理员ID" }],
    bodyParams: [{ name: "status", type: "number", required: true, description: "状态（0-禁用，1-启用）" }],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
    ],
    requiredPermission: "msadmin:admins:update",
  },
  {
    id: "admin-roles",
    method: "GET",
    path: "/api/admin/:id/roles",
    name: "获取管理员角色",
    description: "获取指定管理员的角色列表",
    category: API_CATEGORIES.ADMIN,
    pathParams: [{ name: "id", type: "string", required: true, description: "管理员ID" }],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data", type: "array", description: "角色列表" },
      { name: "data[].id", type: "string", description: "角色ID" },
      { name: "data[].name", type: "string", description: "角色名称" },
      { name: "data[].code", type: "string", description: "角色代码" },
    ],
    requiredPermission: "msadmin:admins:view",
  },
  {
    id: "admin-assign-roles",
    method: "POST",
    path: "/api/admin/:id/roles",
    name: "为管理员分配角色",
    description: "为指定管理员分配角色",
    category: API_CATEGORIES.ADMIN,
    pathParams: [{ name: "id", type: "string", required: true, description: "管理员ID" }],
    bodyParams: [{ name: "role_ids", type: "array", required: true, description: "角色ID数组", example: "[\"1\", \"2\"]" }],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
    ],
    requiredPermission: "msadmin:admins:assign-roles",
  },
  {
    id: "admin-roles-with-permissions",
    method: "GET",
    path: "/api/admin/:id/roles-with-permissions",
    name: "获取管理员角色及权限",
    description: "获取指定管理员的角色列表及其权限",
    category: API_CATEGORIES.ADMIN,
    pathParams: [{ name: "id", type: "string", required: true, description: "管理员ID" }],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data", type: "array", description: "角色及权限列表" },
    ],
    requiredPermission: "msadmin:admins:view",
  },
  {
    id: "admin-extra-permissions",
    method: "GET",
    path: "/api/admin/:id/extra-permissions",
    name: "获取管理员额外权限",
    description: "获取指定管理员的额外权限（非角色权限）",
    category: API_CATEGORIES.ADMIN,
    pathParams: [{ name: "id", type: "string", required: true, description: "管理员ID" }],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data", type: "array", description: "额外权限列表" },
    ],
    requiredPermission: "msadmin:admins:view",
  },
  {
    id: "admin-all-permissions",
    method: "GET",
    path: "/api/admin/:id/all-permissions",
    name: "获取管理员所有权限",
    description: "获取指定管理员的所有权限（包括角色权限和额外权限）",
    category: API_CATEGORIES.ADMIN,
    pathParams: [{ name: "id", type: "string", required: true, description: "管理员ID" }],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
      { name: "data", type: "array", description: "所有权限列表" },
    ],
    requiredPermission: "msadmin:admins:view",
  },
  {
    id: "admin-assign-extra-permissions",
    method: "POST",
    path: "/api/admin/assign-extra-permissions",
    name: "为管理员分配额外权限",
    description: "为指定管理员分配额外权限",
    category: API_CATEGORIES.ADMIN,
    bodyParams: [
      { name: "admin_id", type: "string", required: true, description: "管理员ID" },
      { name: "permission_ids", type: "array", required: true, description: "权限ID数组", example: "[\"1\", \"2\"]" },
    ],
    responseFields: [
      { name: "success", type: "boolean", description: "请求是否成功" },
      { name: "message", type: "string", description: "响应消息" },
    ],
    requiredPermission: "msadmin:admins:update",
  },
];
